<template>
  <layout-report-with-banner :isProgressDone="true" :showFooter="true" :showLoginButton="showLoginButton">
    <template v-slot:banner>
      <v-img :src="headerImage" height="450" class="hidden-xs-only">
        <v-overlay value="true" absolute>
          <v-container class="mx-auto">
            <v-row>
              <v-col class="ml-4">
                <h1 class="text-md-h2 text-sm-h3 text-h4 font-weight-medium mt-sm-8 mt-md-0" v-html="$t(slogan)" />
              </v-col>
            </v-row>
            <v-row class="mt-8">
              <v-col xl="4" lg="5" md="6" xs="10" sm="8" cols="10">
                <v-btn
                  class="ml-4"
                  :color="color"
                  id="start"
                  elevation="0"
                  block
                  dark
                  x-large
                  @click="start"
                  data-test-report-start-desktop-only
                >
                  {{ $t(headerButtonText) }}
                </v-btn>
              </v-col>
            </v-row>
            <div class="text-caption mb-4 mt-4 mx-4">
              <v-icon small>mdi-shield-check</v-icon> {{ $t("report.trustBanner.privacy") }}
              <v-icon small>mdi-lock</v-icon> {{ $t("report.trustBanner.encryption") }}
              <v-icon small>mdi-check-decagram</v-icon>
              {{ $t("report.trustBanner.datacenter") }}
            </div>
          </v-container>
        </v-overlay>
      </v-img>
    </template>
    <template v-slot:content>
      <v-row v-if="!isMobile" align="center" justify="center" class="mt-md-4">
        <v-col md="6" cols="12">
          <v-card flat>
            <v-card-title
              class="text-h4 font-weight-black"
              :style="`color:${partnerColorDarkmode}; word-break: break-word`"
            >
              {{ $t(title) }}
            </v-card-title>

            <v-card-text class="text-body-1 pt-4">
              <v-row v-for="(item, i) in items" :key="i">
                <v-col cols="1">
                  <v-icon>{{ item.icon }}</v-icon>
                </v-col>
                <v-col cols="11" class="pl-6 pl-sm-2">
                  <p>{{ $t(item.text) }}</p>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions class="px-4 py-4">
              <login-register-button :showRegisterButton="false" htmlClass="mr-2" />
              <v-btn :color="color" id="start" elevation="0" dark large @click="start" data-test-report-start>
                {{ $t(buttonText) }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
        <v-col md="6" cols="12">
          <v-card flat>
            <v-card-subtitle class="d-flex d-sm-none" v-if="videoSrc">Lernen Sie uns kennen</v-card-subtitle>
            <v-card-text class="text-center">
              <video controls style="max-width: 100%; max-height: 100%" v-if="videoSrc">
                <source :src="videoSrc" type="video/mp4" />
              </video>
              <v-img v-else :src="imageSrc" class="ma-8 hidden-sm-and-down" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-card :flat="true" v-else>
        <v-row align="stretch">
          <v-col class="d-flex justify-center align-center" cols="12">
            <v-avatar class="mt-4" :color="color" size="80">
              <v-avatar dark x-large>
                <img :src="icon" />
              </v-avatar>
            </v-avatar>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <h1 :class="['headline text-h4 font-weight-bold text-center']">
              {{ $t("report.home.mobile.title") }}
            </h1>
            <div class="text-caption ma-4 text-center">
              <span class="mr-1">
                {{ partner.companyName }}
              </span>
              <span> - </span>
              <span v-if="partner.address" class="ml-1">
                {{ partner.address.street }},
                {{ partner.address.zip }}
                {{ partner.address.city }}
              </span>
            </div>
          </v-col>
        </v-row>
        <v-card-text>
          <v-row style="min-height: 290px" class="d-flex flex-column" align="center" justify="center">
            <v-list class="mobile-report-list">
              <v-list-item v-for="item in items" :key="item.text">
                <v-list-item-icon>
                  <v-icon>
                    {{ item.icon }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content class="text-left">
                  <v-list-item-title class="list-item-title-wrap">{{ $t(item.text) }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-row>
        </v-card-text>

        <v-card-actions class="flex-column">
          <v-btn large @click="start" block :color="color" dark elevation="0" class="ma-1">
            {{ $t("report.home.mobile.btn") }}
          </v-btn>

          <v-btn
            large
            @click="goToLogin"
            block
            :color="color"
            outlined
            elevation="0"
            v-if="showLoginButton"
            class="ma-1"
          >
            {{ $t("report.home.login") }}
          </v-btn>

          <v-bottom-sheet v-model="dialog" inset max-width="500px" scrollable>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ma-1" v-bind="attrs" v-on="on" block :color="color" large dark text elevation="0">
                {{ $t("navCategory.help") }}
              </v-btn>
            </template>
            <instruction-card
              :instructionTitle="instruction.title"
              :instructionText="instruction.text"
              :instructionImageSrcs="instruction.imageSrc"
              :instructionVideoSrc="instruction.videoSrc"
              @clicked="dialog = false"
            />
          </v-bottom-sheet>
        </v-card-actions>
      </v-card>
      <query-params-list />
    </template>
  </layout-report-with-banner>
</template>

<script lang="ts">
import InstructionCard from "@/components/instructions/InstructionCard.vue";
import QueryParamsList from "@/components/settings/QueryParamsList.vue";
import LoaderReport from "@/components/utility/LoaderReport.vue";
import LoginRegisterButton from "@/components/utility/LoginRegisterButton.vue";
import LayoutReportWithBanner from "@/layouts/LayoutReportWithBanner.vue";
import { AssetEnum, AssetRepository } from "@/lib/AssetRepository";
import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { ApplicationInsightsEventEnum } from "@/lib/enum/application-insights-event.enum";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { IReportInstructionData } from "@/lib/interfaces/Report/IReportInstructionData";
import ReportAsyncOperationMixin from "@/mixins/ReportAsyncOperationMixin.vue";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { ConfigModule } from "@/store/modules/config";
import { FeatureModule } from "@/store/modules/feature.store";
import { UserModule } from "@/store/modules/me-user.store";
import { PartnerModule } from "@/store/modules/partner";
import { ReportModule } from "@/store/modules/report.store";
import { Component, Watch } from "vue-property-decorator";
import { debounce } from "debounce";

@Component({
  components: {
    LayoutReportWithBanner,
    LoaderReport,
    LoginRegisterButton,
    QueryParamsList: QueryParamsList,
    InstructionCard
  }
})
export default class ReportHome extends ReportAsyncOperationMixin {
  dialog = false;
  private trackOpenDialogDebounced = debounce(() => this.trackOpenDialog(), 30000, true);

  @Watch("dialog")
  watchDialog(newVal: boolean) {
    if (newVal) {
      this.trackOpenDialogDebounced();
    }
  }

  trackOpenDialog() {
    this.$appInsights.trackEvent(
      { name: ApplicationInsightsEventEnum.OPEN_REPORT_HELP_DIALOG },
      { partnerId: PartnerModule.partner.id }
    );
  }

  get showLoginButton(): boolean {
    const isCustomerSignup = PartnerModule.partner?.settings?.bookedServices.customer ?? false;
    const isThg = ConfigModule.appContext === AppContextEnum.THG;
    return isThg ? !UserModule.isAuthenticated : !UserModule.isAuthenticated && isCustomerSignup;
  }

  get loading() {
    return ReportModule.loading;
  }

  get headerImage() {
    if (PartnerModule?.partner?.settings?.reportSettings?.headerImageUrl) {
      return PartnerModule?.partner?.settings?.reportSettings?.headerImageUrl;
    } else if (PartnerModule?.partner?.settings?.headerImageUrl) {
      return PartnerModule?.partner?.settings?.headerImageUrl;
    } else {
      return "https://mmmint.azureedge.net/wirkaufendeinethg/header-image.png";
    }
  }

  get videoSrc() {
    return PartnerModule?.partner?.settings?.reportSettings?.welcomeVideoUrl;
  }

  get imageSrc() {
    if (PartnerModule?.partner?.settings?.reportSettings?.welcomeImageUrl) {
      return PartnerModule?.partner?.settings?.reportSettings?.welcomeImageUrl;
    }

    return AssetRepository.getAsset(false, AssetEnum.towingTruck);
  }

  get title() {
    if (PartnerModule?.partner?.settings?.reportSettings?.welcomeTitle) {
      return PartnerModule?.partner?.settings?.reportSettings?.welcomeTitle;
    }

    return "report.home.title";
  }

  get items() {
    if (
      PartnerModule?.partner?.settings?.reportSettings?.welcomeText &&
      PartnerModule?.partner?.settings?.reportSettings?.welcomeText.length >= 1
    ) {
      return PartnerModule?.partner?.settings?.reportSettings?.welcomeText;
    }

    return [
      { text: "report.home.step1", icon: "mdi-camera" },
      { text: "report.home.step2", icon: "mdi-account" },
      { text: "report.home.step3", icon: "mdi-calendar-check" }
    ];
  }

  get slogan() {
    if (PartnerModule?.partner?.settings?.reportSettings?.headerTitle) {
      return PartnerModule?.partner?.settings?.reportSettings?.headerTitle;
    }

    return "report.home.slogan";
  }

  get headerButtonText() {
    if (PartnerModule?.partner?.settings?.reportSettings?.headerBtn) {
      return PartnerModule?.partner?.settings?.reportSettings?.headerBtn;
    }

    return "report.home.start";
  }

  get buttonText() {
    if (PartnerModule?.partner?.settings?.reportSettings?.welcomeBtn) {
      return PartnerModule?.partner?.settings?.reportSettings?.welcomeBtn;
    }

    return "report.home.start";
  }

  get loginButtonText() {
    return "report.home.login";
  }

  start() {
    if (ReportModule.nextScreen) {
      const next = ReportModule.nextScreen;
      ReportModule.setStep(next);
      this.$router.push({ path: `/${next}` });
    }
  }

  get color() {
    return getDefaultPartnerColor();
  }

  get partnerColorDarkmode() {
    return this.$vuetify.theme.dark ? "white" : this.color;
  }

  get partner() {
    return PartnerModule.partner;
  }

  get isMobile() {
    return FeatureModule.isReportNewMobileView && this.$vuetify.breakpoint.mobile;
  }

  get instruction(): IReportInstructionData {
    return {
      title: this.title ?? "components.DropField.instructions",
      text: this.$t("report.home.mobile.text", { screenNames: "- " + this.screenNames?.join("<br/> - ") }).toString(),
      imageSrc: [this.imageSrc],
      videoSrc: this.videoSrc,
      display: true
    };
  }

  get screens() {
    const order = ReportModule.screenOder;

    return order ? order.slice(1, order.length - 1) : order;
  }

  get screenNames() {
    return this.screens?.map((s) => this.$t(`screenEnum.${s}.title`)) ?? [];
  }

  get icon() {
    return AssetRepository.getAsset(false, AssetEnum.icon);
  }

  async mounted() {
    // Handle special case if partnerId by route e.g. `/partners/:partnerId/app/report`
    if (this.$route.params.partnerId) {
      try {
        await ReportModule.getPartnerById(this.$route.params.partnerId);
      } catch (error) {
        this.$log.error(error);
        this.$toast("Fehler beim Laden der Anwendung.");
        this.$router.push({ name: "PartnerContactError" });
      }
    }

    // Handle special case if partner by route e.g. `/partner/mmmint.ai/report`.
    if (this.$route.params.partnerName) {
      try {
        await ReportModule.getPartnerByName(this.$route.params.partnerName);
      } catch (error) {
        this.$log.error(error);
        this.$toast("Fehler beim Laden der Anwendung.");
        this.$router.push({ name: "PartnerContactError" });
      }
    }

    this.startAsyncOperations().then(() => {
      this.$log.info("async operations finished");
    });

    ReportModule.setStep(ReportModule.firstScreen);
    if (ReportModule.firstScreen != ReportScreenEnum.welcome) {
      this.$router.push({ path: `/${ReportModule.firstScreen}` });
    }
  }

  goToLogin() {
    this.$router.push({ path: "/login" });
  }
}
</script>

<style scoped>
.center > i {
  position: relative;
  left: 50%;
}

.light > span {
  font-weight: 400;
}

.padded {
  padding-top: 1.5em;
}

h1 {
  font-size: unset;
}

.mobile-report-list .v-list-item {
  flex-wrap: wrap;
  align-items: flex-start;
}

.mobile-report-list .v-list-item-content {
  white-space: normal;
}

.list-item-title-wrap {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}
</style>
